import type { FieldPath, FieldValues } from 'react-hook-form';
import { cn } from '@/shared/lib/utils';
import { type FormFieldSize, formFieldSizeVariants } from '../helpers/field-size';
import { FieldItem, type FieldItemProps } from './FieldItem';

/**
 * FieldItemPropsからオプショナルにするプロパティのキー
 * FormFieldWrapperでは、これらのプロパティを任意にできるようにする
 */
type OptionalFieldKeys =
  | 'required'
  | 'supportText'
  | 'showSupportText'
  | 'readOnly'
  | 'displayValue'
  | 'className';

/**
 * FormFieldWrapper のプロパティ型
 *
 * FieldItemPropsをベースに、以下の変更を加える:
 * - OptionalFieldKeysで指定されたプロパティをオプショナルにする
 * - sizeプロパティを追加してフィールドの最小幅を指定可能にする
 */
export type FormFieldWrapperProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<FieldItemProps<TFieldValues, TName>, OptionalFieldKeys> & {
  [Key in OptionalFieldKeys]?: FieldItemProps<TFieldValues, TName>[Key] | undefined;
} & {
  /** フィールドの最小幅（デフォルト: sm） */
  size?: FormFieldSize;
};

/**
 * FormFieldWrapper コンポーネント
 *
 * FieldItemをラップし、以下の機能を提供:
 * - フィールドのサイズ（最小幅）を指定可能にする
 * - 一部のプロパティをオプショナルにして使いやすくする
 * - サイズに応じたスタイルを自動適用
 *
 * 使用例:
 * ```tsx
 * <FormFieldWrapper
 *   control={form.control}
 *   name="email"
 *   label="メールアドレス"
 *   size="md"
 *   required
 * >
 *   {({ field, fieldId, hasError }) => (
 *     <Input {...field} id={fieldId} />
 *   )}
 * </FormFieldWrapper>
 * ```
 */
export const FormFieldWrapper = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  size = 'sm',
  className,
  children,
  required,
  supportText,
  showSupportText,
  readOnly,
  displayValue,
  ...fieldItemProps
}: FormFieldWrapperProps<TFieldValues, TName>) => (
  <FieldItem
    {...fieldItemProps}
    {...(required !== undefined && { required })}
    {...(supportText !== undefined && { supportText })}
    {...(showSupportText !== undefined && { showSupportText })}
    {...(readOnly !== undefined && { readOnly })}
    {...(displayValue !== undefined && { displayValue })}
    className={cn(formFieldSizeVariants({ size }), className)}
  >
    {children}
  </FieldItem>
);

FormFieldWrapper.displayName = 'FormFieldWrapper';
